
import React, { useEffect, useState, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useParams, Link, useNavigate } from 'react-router-dom';
import clientService, { BrandKitUpdate, BrandKitResponse } from '../services/clientService';
import mediaService, { MediaFile, UploadResponse } from '../services/mediaService';
import { motion } from 'framer-motion';
import { ArrowLeft, Save, Loader2, Mic, Globe, Palette, Image as ImageIcon, Upload, X, BookOpen, Plus, ChevronDown, Check } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

import { useClient } from '../contexts/ClientContext';
import brandTemplateService, { BrandTemplate } from '../services/brandTemplateService';
import AuthenticatedImage from '../components/media/AuthenticatedImage';

const InputField = ({ name, label, required, register, errors, placeholder, type = 'text', disabled = false, icon: Icon = null }) => (
  <div>
    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3">{label}{required && <span className="text-red-500 ml-1">*</span>}</label>
    <div className="relative group">
      {Icon && (
        <div className="absolute left-4 top-1/2 -translate-y-1/2 z-10">
          <Icon className="w-5 h-5 text-slate-400 dark:text-slate-500 group-focus-within:text-indigo-500 dark:group-focus-within:text-indigo-400 transition-colors duration-200" />
        </div>
      )}
      <input
        {...register(name, { required: required && `${label} is required` })}
        type={type}
        placeholder={placeholder}
        disabled={disabled}
        className={`w-full ${Icon ? 'pl-12' : 'pl-4'} pr-4 py-2.5 bg-white/80 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 dark:focus:border-indigo-400 transition-all duration-200 text-slate-900 dark:text-slate-100 placeholder-slate-500 dark:placeholder-slate-400 shadow-sm hover:shadow-md disabled:bg-slate-100 dark:disabled:bg-slate-800/50 disabled:cursor-not-allowed backdrop-blur-sm ${errors[name] ? 'border-red-500 focus:ring-red-500/50' : ''}`}
      />
    </div>
    {errors[name] && <p className="mt-2 text-sm text-red-500 dark:text-red-400 flex items-center"><span className="mr-1">⚠️</span>{errors[name].message}</p>}
  </div>
);

const TextAreaField = ({ name, label, required, register, errors, placeholder, icon: Icon }) => (
  <div>
    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3">{label}{required && <span className="text-red-500 ml-1">*</span>}</label>
    <div className="relative group">
      <div className="absolute left-4 top-4 z-10">
        <Icon className="w-5 h-5 text-slate-400 dark:text-slate-500 group-focus-within:text-indigo-500 dark:group-focus-within:text-indigo-400 transition-colors duration-200" />
      </div>
      <textarea
        {...register(name, { required: required && `${label} is required` })}
        rows={3}
        placeholder={placeholder}
        className={`w-full pl-12 pr-4 py-2.5 bg-white/80 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 dark:focus:border-indigo-400 transition-all duration-200 text-slate-900 dark:text-slate-100 placeholder-slate-500 dark:placeholder-slate-400 shadow-sm hover:shadow-md backdrop-blur-sm resize-none ${errors[name] ? 'border-red-500 focus:ring-red-500/50' : ''}`}
      />
    </div>
    {errors[name] && <p className="mt-2 text-sm text-red-500 dark:text-red-400 flex items-center"><span className="mr-1">⚠️</span>{errors[name].message}</p>}
  </div>
);

const ModernDropdown = ({ name, label, options, control, placeholder, icon: Icon, optgroups = null, maxHeight = "max-h-80" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState('');
  const [selectedLabel, setSelectedLabel] = useState('');
  const dropdownRef = React.useRef(null);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div>
      <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3">{label}</label>
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          const handleSelect = (value, label) => {
            field.onChange(value);
            setSelectedValue(value);
            setSelectedLabel(label);
            setIsOpen(false);
          };

          // Update display when field value changes
          React.useEffect(() => {
            if (field.value) {
              setSelectedValue(field.value);
              // Find the label for the current value
              if (optgroups) {
                for (const group of optgroups) {
                  const option = group.options.find(opt => opt.value === field.value);
                  if (option) {
                    setSelectedLabel(option.label);
                    break;
                  }
                }
              } else {
                const option = options.find(opt => opt.value === field.value);
                if (option) {
                  setSelectedLabel(option.label);
                }
              }
            } else {
              setSelectedValue('');
              setSelectedLabel('');
            }
          }, [field.value]);

          return (
            <div className="relative" ref={dropdownRef}>
              {/* Dropdown Button */}
              <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full pl-12 pr-10 py-2.5 bg-white/80 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 dark:focus:border-indigo-400 transition-all duration-200 text-slate-900 dark:text-slate-100 shadow-sm hover:shadow-md backdrop-blur-sm text-left flex items-center justify-between group"
              >
                <div className="flex items-center">
                  <div className="absolute left-4">
                    <Icon className="w-5 h-5 text-slate-400 dark:text-slate-500 group-focus:text-indigo-500 dark:group-focus:text-indigo-400 transition-colors duration-200" />
                  </div>
                  <span className={selectedLabel ? 'text-slate-900 dark:text-slate-100' : 'text-slate-500 dark:text-slate-400'}>
                    {selectedLabel || placeholder}
                  </span>
                </div>
                <ChevronDown className={`w-5 h-5 text-slate-400 dark:text-slate-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown Menu */}
              {isOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.15 }}
                  className={`absolute z-50 w-full mt-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl shadow-xl backdrop-blur-xl ${maxHeight} overflow-y-auto`}
                >
                  {optgroups ? (
                    // Render optgroups
                    optgroups.map((group, groupIndex) => (
                      <div key={groupIndex}>
                        {group.label && (
                          <div className="px-4 py-2 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider bg-slate-50 dark:bg-slate-700/50 border-b border-slate-100 dark:border-slate-700">
                            {group.label}
                          </div>
                        )}
                        {group.options.map((option, optionIndex) => (
                          <button
                            key={`${groupIndex}-${optionIndex}`}
                            type="button"
                            onClick={() => handleSelect(option.value, option.label)}
                            className="w-full px-4 py-2.5 text-left hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors duration-150 flex items-center justify-between group"
                          >
                            <span className={`text-sm ${option.value === '' ? 'text-slate-500 dark:text-slate-400 italic' : 'text-slate-900 dark:text-slate-100'}`}>{option.label}</span>
                            {selectedValue === option.value && (
                              <Check className="w-4 h-4 text-indigo-500 dark:text-indigo-400" />
                            )}
                          </button>
                        ))}
                      </div>
                    ))
                  ) : (
                    // Render simple options
                    options.map((option, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleSelect(option.value, option.label)}
                        className="w-full px-4 py-2.5 text-left hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors duration-150 flex items-center justify-between group"
                      >
                        <span className={`text-sm ${option.value === '' ? 'text-slate-500 dark:text-slate-400 italic' : 'text-slate-900 dark:text-slate-100'}`}>{option.label}</span>
                        {selectedValue === option.value && (
                          <Check className="w-4 h-4 text-indigo-500 dark:text-indigo-400" />
                        )}
                      </button>
                    ))
                  )}
                </motion.div>
              )}


            </div>
          );
        }}
      />
    </div>
  );
};

const ColorPicker = ({ name, label, control, defaultValue }) => {
  const [showHexInput, setShowHexInput] = useState(false);
  const [hexValue, setHexValue] = useState('');
  const [showRgbInput, setShowRgbInput] = useState(false);
  const [rgbValue, setRgbValue] = useState({ r: 0, g: 0, b: 0 });
  const { toast } = useToast();

  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  const rgbToHex = (r, g, b) => {
    return "#" + [r, g, b].map(x => {
      const hex = x.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    }).join('');
  };

  return (
    <div className="relative">
      <label className="block text-xs font-medium text-slate-700 dark:text-slate-200 mb-2 text-center">{label}</label>
      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue}
        render={({ field }) => (
          <div className="space-y-2">
            <div className="relative w-14 h-14 mx-auto rounded-xl overflow-hidden border-2 border-slate-200 dark:border-slate-600 shadow-md hover:shadow-lg transition-all duration-300 group">
              <input 
                type="color" 
                {...field} 
                className="absolute inset-0 w-full h-full cursor-pointer z-20"
                style={{ opacity: 0 }}
              />
              <div 
                className="absolute inset-0 w-full h-full rounded-lg transition-all duration-300 group-hover:scale-105 pointer-events-none"
                style={{ backgroundColor: field.value || '#E2E8F0' }}
              ></div>
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300 rounded-lg flex items-center justify-center pointer-events-none">
                <Palette className="w-4 h-4 text-white/0 group-hover:text-white/80 transition-all duration-300" />
              </div>
            </div>
            
            {/* Hex Input Toggle */}
            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  setShowHexInput(!showHexInput);
                  if (!showHexInput) {
                    setHexValue(field.value || '#E2E8F0');
                  }
                  setShowRgbInput(false);
                }}
                className="text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
              >
                {showHexInput ? 'Hide' : 'Edit'} Hex
              </button>
              <span className="text-xs text-slate-400 mx-1">|</span>
              <button
                type="button"
                onClick={() => {
                  setShowRgbInput(!showRgbInput);
                  if (!showRgbInput) {
                    const rgb = hexToRgb(field.value || '#E2E8F0');
                    if (rgb) {
                      setRgbValue(rgb);
                    }
                  }
                  setShowHexInput(false);
                }}
                className="text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
              >
                {showRgbInput ? 'Hide' : 'Edit'} RGB
              </button>
            </div>
            
            {/* Hex Input Field */}
            {showHexInput && (
              <div className="space-y-1">
                <input
                  type="text"
                  value={hexValue}
                  onChange={(e) => setHexValue(e.target.value)}
                  onBlur={() => {
                    // Validate and apply hex color
                    const isValidHex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hexValue);
                    if (isValidHex) {
                      field.onChange(hexValue);
                    } else {
                      setHexValue(field.value || '#E2E8F0');
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const isValidHex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hexValue);
                      if (isValidHex) {
                        field.onChange(hexValue);
                        setShowHexInput(false);
                      }
                    }
                    if (e.key === 'Escape') {
                      setHexValue(field.value || '#E2E8F0');
                      setShowHexInput(false);
                    }
                  }}
                  placeholder="#FFFFFF"
                  className="w-full px-2 py-1 text-xs text-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50"
                />
                <div className="flex space-x-1">
                  <button
                    type="button"
                    onClick={() => {
                      const colorValue = field.value || '#E2E8F0';
                      
                      // Try modern clipboard API first, fallback to older method
                      if (navigator.clipboard && navigator.clipboard.writeText) {
                        navigator.clipboard.writeText(colorValue).then(() => {
                          toast({ title: 'Color copied to clipboard!', description: `Copied ${colorValue}` });
                        }).catch((err) => {
                          console.error('Clipboard API failed:', err);
                          // Fallback method
                          const textArea = document.createElement('textarea');
                          textArea.value = colorValue;
                          document.body.appendChild(textArea);
                          textArea.select();
                          document.execCommand('copy');
                          document.body.removeChild(textArea);
                          toast({ title: 'Color copied to clipboard!', description: `Copied ${colorValue}` });
                        });
                      } else {
                        // Fallback for older browsers
                        const textArea = document.createElement('textarea');
                        textArea.value = colorValue;
                        document.body.appendChild(textArea);
                        textArea.select();
                        document.execCommand('copy');
                        document.body.removeChild(textArea);
                        toast({ title: 'Color copied to clipboard!', description: `Copied ${colorValue}` });
                      }
                    }}
                    className="flex-1 px-2 py-1 text-xs bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-300 rounded transition-colors"
                    title="Copy color to clipboard"
                  >
                    Copy
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      // Try modern clipboard API first
                      if (navigator.clipboard && navigator.clipboard.readText) {
                        navigator.clipboard.readText().then((text) => {
                          let cleanText = text.trim();
                          
                          // Add # if missing
                          if (!cleanText.startsWith('#') && /^[A-Fa-f0-9]{3,6}$/.test(cleanText)) {
                            cleanText = '#' + cleanText;
                          }
                          
                          // Validate hex color
                          if (/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(cleanText)) {
                            setHexValue(cleanText);
                            field.onChange(cleanText);
                            toast({ title: 'Color pasted successfully!', description: `Applied ${cleanText}` });
                          } else {
                            toast({ title: 'Invalid color format', description: 'Clipboard does not contain a valid hex color', variant: 'destructive' });
                          }
                        }).catch((err) => {
                          console.error('Clipboard read failed:', err);
                          toast({ title: 'Could not read from clipboard', description: 'Please paste manually in the hex field above.', variant: 'destructive' });
                        });
                      } else {
                        toast({ title: 'Clipboard access not supported', description: 'Please paste manually in the hex field above.', variant: 'destructive' });
                      }
                    }}
                    className="flex-1 px-2 py-1 text-xs bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-300 rounded transition-colors"
                    title="Paste color from clipboard"
                  >
                    Paste
                  </button>
                </div>
              </div>
            )}
            {showRgbInput && (
              <div className="space-y-1">
                <div className="flex space-x-1">
                  <input
                    type="number"
                    min="0"
                    max="255"
                    value={rgbValue.r}
                    onChange={(e) => setRgbValue({ ...rgbValue, r: parseInt(e.target.value) })}
                    onBlur={() => field.onChange(rgbToHex(rgbValue.r, rgbValue.g, rgbValue.b))}
                    className="w-full px-2 py-1 text-xs text-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50"
                  />
                  <input
                    type="number"
                    min="0"
                    max="255"
                    value={rgbValue.g}
                    onChange={(e) => setRgbValue({ ...rgbValue, g: parseInt(e.target.value) })}
                    onBlur={() => field.onChange(rgbToHex(rgbValue.r, rgbValue.g, rgbValue.b))}
                    className="w-full px-2 py-1 text-xs text-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50"
                  />
                  <input
                    type="number"
                    min="0"
                    max="255"
                    value={rgbValue.b}
                    onChange={(e) => setRgbValue({ ...rgbValue, b: parseInt(e.target.value) })}
                    onBlur={() => field.onChange(rgbToHex(rgbValue.r, rgbValue.g, rgbValue.b))}
                    className="w-full px-2 py-1 text-xs text-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50"
                  />
                </div>
                <div className="flex space-x-1">
                  <button
                    type="button"
                    onClick={() => {
                      const colorValue = `rgb(${rgbValue.r}, ${rgbValue.g}, ${rgbValue.b})`;
                      navigator.clipboard.writeText(colorValue).then(() => {
                        toast({ title: 'Color copied to clipboard!', description: `Copied ${colorValue}` });
                      });
                    }}
                    className="flex-1 px-2 py-1 text-xs bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-300 rounded transition-colors"
                    title="Copy color to clipboard"
                  >
                    Copy RGB
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.readText().then((text) => {
                        const rgbMatch = text.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
                        if (rgbMatch) {
                          const r = parseInt(rgbMatch[1]);
                          const g = parseInt(rgbMatch[2]);
                          const b = parseInt(rgbMatch[3]);
                          setRgbValue({ r, g, b });
                          field.onChange(rgbToHex(r, g, b));
                          toast({ title: 'Color pasted successfully!', description: `Applied rgb(${r}, ${g}, ${b})` });
                        } else {
                          toast({ title: 'Invalid color format', description: 'Clipboard does not contain a valid RGB color', variant: 'destructive' });
                        }
                      });
                    }}
                    className="flex-1 px-2 py-1 text-xs bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-300 rounded transition-colors"
                    title="Paste color from clipboard"
                  >
                    Paste RGB
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      />
    </div>
  );
};

const BrandKitPage: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const { currentClient } = useClient();
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<MediaFile | null>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  
  // Template functionality
  const [templates, setTemplates] = useState<BrandTemplate[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [showLoadTemplate, setShowLoadTemplate] = useState(false);
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');

  const { register, handleSubmit, reset, control, setValue, watch, formState: { errors } } = useForm<BrandKitUpdate>({
    defaultValues: {
      brand_name: '',
      brand_colors: { primary: '#6366F1', secondary: '#8B5CF6', accent: '#EC4899', neutral: '#64748B' }
    }
  });
  const brandColors = watch('brand_colors');

  const fetchBrandKit = useCallback(async () => {
    if (!clientId) return;
    try {
      setIsFetching(true);
      const data = await clientService.getBrandKit(clientId);
      
      // Ensure brand_colors has default values if not present
      const formData = {
        ...data,
        brand_colors: data.brand_colors || {
          primary: '#6366F1',
          secondary: '#8B5CF6', 
          accent: '#EC4899',
          neutral: '#64748B'
        }
      };
      
      reset(formData);
      
      if (data.logo_media_id) {
        const logoData = await mediaService.getMediaFile(data.logo_media_id, clientId);
        setLogoFile(logoData);
      }
    } catch (err) {
      setError('Failed to load brand kit data. Please switch to the corresponding brand from the top right dropdown and refresh the page.');
    } finally {
      setIsFetching(false);
    }
  }, [clientId, reset]);

  // Load templates
  const fetchTemplates = useCallback(async () => {
    if (!clientId) return;
    try {
      setIsLoadingTemplates(true);
      const templateData = await brandTemplateService.getTemplates(clientId);
      setTemplates(templateData);
    } catch (err) {
      console.error('Failed to load templates:', err);
    } finally {
      setIsLoadingTemplates(false);
    }
  }, [clientId]);

  // Load template data into form
  const loadTemplate = async (template: BrandTemplate) => {
    const templateData = template.template_data;
    
    // Reset form with template data
    reset({
      brand_name: templateData.brand_name || '',
      brand_voice: templateData.brand_voice || '',

      brand_colors: templateData.brand_colors || {
        primary: '#6366F1',
        secondary: '#8B5CF6', 
        accent: '#EC4899',
        neutral: '#64748B'
      },
      target_audience: templateData.target_audience || '',
      industry: templateData.industry || '',
      primary_goal: templateData.primary_goal || '',
      brand_personality: templateData.brand_personality || []
    });
    
    setSuccess(`Template "${template.name}" loaded successfully!`);
    setTimeout(() => setSuccess(null), 3000);
  };



  useEffect(() => {
    fetchBrandKit();
    fetchTemplates();
  }, [fetchBrandKit, fetchTemplates]);

  const onSubmit = async (data: BrandKitUpdate) => {
    if (!clientId) return;
    
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    // Prepare the update data
    const updateData: BrandKitUpdate = {};
    
    // Only add fields that have actual values
    if (data.brand_name && data.brand_name.trim()) {
      updateData.brand_name = data.brand_name.trim();
    }
    if (data.brand_voice && data.brand_voice.trim()) {
      updateData.brand_voice = data.brand_voice.trim();
    }
    

    
    // Handle logo - explicitly set to null if removed, or set to the file ID if present
    if (logoFile?.id) {
      updateData.logo_media_id = logoFile.id;
    } else {
      updateData.logo_media_id = null;
    }
    
    // Include brand colors
    if (data.brand_colors) {
      updateData.brand_colors = data.brand_colors;
    }

    // Include enhanced brand kit fields (always include them, even if empty)
    updateData.target_audience = data.target_audience ? data.target_audience.trim() : null;
    
    // Handle industry field
    if (data.industry === 'Other' && data.custom_industry && data.custom_industry.trim()) {
      updateData.industry = data.custom_industry.trim();
    } else {
      updateData.industry = data.industry && data.industry.trim() ? data.industry.trim() : null;
    }
    
    updateData.primary_goal = data.primary_goal ? data.primary_goal.trim() : null;
    updateData.brand_personality = data.brand_personality || [];

    console.log('DEBUG: Sending brand kit data:', updateData);
    console.log('DEBUG: Client ID:', clientId);

    try {
      const response = await clientService.updateBrandKit(clientId, updateData);
      
      // Update form with response data to ensure consistency
      const formData = {
        ...response,
        brand_colors: response.brand_colors || {
          primary: '#6366F1',
          secondary: '#8B5CF6', 
          accent: '#EC4899',
          neutral: '#64748B'
        }
      };
      
      reset(formData);
      
      if (response.logo_media_id) {
        const logoData = await mediaService.getMediaFile(response.logo_media_id, clientId);
        setLogoFile(logoData);
      }
      
      // Save as template if checkbox is checked
      if (saveAsTemplate && templateName.trim()) {
        try {
          await brandTemplateService.createTemplate(clientId, {
            name: templateName.trim(),
            description: `Brand kit template created on ${new Date().toLocaleDateString()}`,
            template_data: data
          });
          setSuccess('Brand kit updated and saved as template successfully!');
          fetchTemplates(); // Refresh templates list
        } catch (templateErr) {
          console.error('Failed to save template:', templateErr);
          setSuccess('Brand kit updated successfully, but failed to save template');
        }
      } else {
        setSuccess('Brand kit updated successfully!');
      }
      
      // Reset template form
      setSaveAsTemplate(false);
      setTemplateName('');
      
      // Navigate back to client page immediately after success
      navigate('/app/clients');
      
    } catch (err: any) {
      console.error('Brand kit update error:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to update brand kit';
      setError(typeof errorMessage === 'string' ? errorMessage : 'Failed to update brand kit');
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) {
    return (
      <div className="flex justify-center items-center h-screen bg-slate-50 dark:bg-slate-900">
        <div className="w-16 h-16 border-4 border-slate-300/30 dark:border-slate-600/30 border-t-indigo-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 dark:bg-slate-900 dark:text-white">
      <div className="backdrop-blur-2xl bg-white/60 dark:bg-slate-900/60 border-b border-slate-200/30 dark:border-slate-700/30">
        <div className="px-8 py-6">
          <div className="flex items-center justify-between">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
              <div className="flex items-center space-x-4">
                <Link to={`/app/clients`}>
                  <motion.button whileHover={{ scale: 1.1, x: -2 }} className="p-2 rounded-full bg-white/50 dark:bg-slate-800/50 hover:bg-slate-100/70 dark:hover:bg-slate-700/70 border border-slate-300/50 dark:border-slate-600/50">
                    <ArrowLeft className="w-6 h-6 text-slate-600 dark:text-slate-300" />
                  </motion.button>
                </Link>
                <div>
                  <h1 className="text-4xl font-black bg-gradient-to-r from-slate-800 via-purple-600 to-indigo-600 dark:from-white dark:via-purple-200 dark:to-indigo-200 bg-clip-text text-transparent">
                    Brand Kit
                  </h1>
                  <p className="text-slate-500 dark:text-slate-300 text-lg">Manage your client's brand assets</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <div className="px-8 py-8">
        <motion.div 
          className="max-w-4xl mx-auto bg-white/90 dark:bg-slate-800/90 backdrop-blur-xl border border-slate-200/60 dark:border-slate-700/60 rounded-3xl p-6 shadow-xl shadow-slate-200/50 dark:shadow-slate-900/50"
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
        >
          {error && (
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 shadow-sm"
            >
              <p className="text-red-700 dark:text-red-300 font-semibold flex items-center">
                <span className="mr-2">⚠️</span>
                {typeof error === 'string' ? error : 'An error occurred while saving the brand kit'}
              </p>
            </motion.div>
          )}
          

          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mb-8 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800/50 rounded-2xl p-5 shadow-sm"
          >
            <div className="flex">
              <div className="flex-shrink-0 pt-0.5">
                <svg className="h-6 w-6 text-indigo-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.852l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
                </svg>
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-base font-bold text-indigo-800 dark:text-indigo-200">
                  Get Better Results
                </h3>
                <p className="mt-1 text-sm text-indigo-700 dark:text-indigo-300">
                  Providing brand information is optional, but highly recommended. It helps our AI tailor content to your specific brand identity. If left blank, our AI-powered tools will generate content based on your uploaded images and chosen parameters.
                </p>
              </div>
            </div>
          </motion.div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">

            {/* Load Template Button */}
            {templates.length > 0 && (
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={() => setShowLoadTemplate(!showLoadTemplate)}
                  className="flex items-center px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg transition-colors border border-blue-200 dark:border-blue-800"
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  Load Template
                </button>
              </div>
            )}

            {/* Load Template Dropdown */}
            {showLoadTemplate && (
              <div className="bg-blue-50 dark:bg-slate-800/50 rounded-lg p-4 border border-blue-200 dark:border-slate-600">
                <select
                  onChange={(e) => {
                    const templateId = e.target.value;
                    if (templateId) {
                      const template = templates.find(t => t.id === templateId);
                      if (template) {
                        loadTemplate(template);
                        setShowLoadTemplate(false);
                      }
                    }
                  }}
                  className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500"
                  disabled={isLoadingTemplates}
                >
                  <option value="">
                    {isLoadingTemplates ? 'Loading templates...' : 'Select a template to load...'}
                  </option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">Brand Voice & Tone</label>
              <div className="relative group">
                <div className="absolute left-4 top-3 z-10">
                  <BookOpen className="w-4 h-4 text-slate-400 dark:text-slate-500 group-focus-within:text-indigo-500 dark:group-focus-within:text-indigo-400 transition-colors duration-200" />
                </div>
                <textarea
                  {...register('brand_voice')}
                  rows={3}
                  placeholder="Describe the brand's personality, tone of voice, mission, and any key slogans..."
                  className="w-full pl-11 pr-4 py-3 bg-white/80 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 dark:focus:border-indigo-400 transition-all duration-200 text-slate-900 dark:text-slate-100 placeholder-slate-500 dark:placeholder-slate-400 shadow-sm hover:shadow-md backdrop-blur-sm resize-none"
                />
              </div>
              {errors.brand_voice && <p className="mt-1 text-sm text-red-500 dark:text-red-400">⚠️ {errors.brand_voice.message}</p>}
            </div>

            <div className="grid grid-cols-1 gap-6">
              <InputField name="brand_name" label="Brand Name" register={register} errors={errors} placeholder="Enter your brand name here" />
            </div>

            <div className="bg-slate-50/50 dark:bg-slate-700/30 rounded-xl p-3 border border-slate-200/50 dark:border-slate-600/50">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Logo Section */}
                <div>
                  <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100 mb-3 flex items-center justify-center">
                    <div className="w-5 h-5 bg-gradient-to-br from-green-500 to-blue-600 rounded-lg flex items-center justify-center mr-2 shadow-md">
                      <ImageIcon className="w-3 h-3 text-white"/>
                    </div>
                    Logo
                  </h3>
                  <div className="h-32 flex items-center justify-center">

                    {logoFile ? (
                                               <div className="relative group w-28 h-28">                         <div className="w-28 h-28 rounded-lg border-2 border-slate-200 dark:border-slate-600 bg-slate-100 dark:bg-slate-700 flex items-center justify-center shadow-md overflow-hidden">
                           {logoFile.thumbnail_url || logoFile.file_url ? (
                             <AuthenticatedImage 
                                src={logoFile.thumbnail_url || logoFile.file_url}
                                alt={logoFile.original_filename || 'Brand logo'}
                                className="w-full h-full object-cover"
                                clientId={clientId}
                                onError={() => {
                                  console.error('Image failed to load:', logoFile);
                                }}
                             />
                           ) : null}
                           <div className="w-full h-full flex items-center justify-center" style={{ display: logoFile.thumbnail_url || logoFile.file_url ? 'none' : 'flex' }}>
                             <ImageIcon className="w-8 h-8 text-slate-400" />
                           </div>
                         </div>
                         <button 
                            onClick={() => setLogoFile(null)} 
                            className="absolute top-1 right-1 p-1 bg-black/60 dark:bg-slate-900/80 rounded-full text-white hover:bg-red-500 transition-all duration-200 opacity-0 group-hover:opacity-100 shadow-lg"
                          >
                           <X className="w-3 h-3"/>
                         </button>
                      </div>
                    ) : (
                      <div className="relative">
                        <input
                          type="file"
                          accept="image/*"
                          disabled={isUploadingLogo}
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (file && clientId) {
                              try {
                                setIsUploadingLogo(true);
                                setError(null);
                                console.log('Starting upload for file:', file.name, 'with clientId:', clientId);
                                
                                const uploadResponse = await mediaService.uploadBrandKitAsset(file, clientId);
                                console.log('Full upload response:', uploadResponse);
                                
                                if (uploadResponse && uploadResponse.file) {
                                  const uploadedFile = uploadResponse.file;
                                  console.log('Extracted media file:', uploadedFile);
                                  setLogoFile(uploadedFile);
                                } else if (uploadResponse && uploadResponse.media_file) {
                                  const uploadedFile = uploadResponse.media_file;
                                  console.log('Extracted media file (media_file):', uploadedFile);
                                  setLogoFile(uploadedFile);
                                } else {
                                  console.error('No file found in response:', uploadResponse);
                                  setError('Upload failed - no file in response');
                                }
                              } catch (error) {
                                console.error('Error uploading logo:', error);
                                setError(`Failed to upload logo: ${error.message || error}`);
                              } finally {
                                setIsUploadingLogo(false);
                              }
                            }
                          }}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10 disabled:cursor-not-allowed"
                        />
                        <div className="w-28 h-28 bg-slate-50 dark:bg-slate-800/80 border-2 border-dashed border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-400 rounded-lg hover:border-indigo-400 dark:hover:border-indigo-500 hover:bg-slate-100 dark:hover:bg-slate-700/80 transition-all duration-300 shadow-sm text-xs flex flex-col items-center justify-center">
                          {isUploadingLogo ? (
                            <>
                              <Loader2 className="w-6 h-6 mb-1 animate-spin" />
                              <span>Uploading...</span>
                            </>
                          ) : (
                            <>
                              <Upload className="w-6 h-6 mb-1" />
                              <span>Upload Logo</span>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Brand Colors Section */}
                <div>
                  <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100 mb-3 flex items-center justify-center">
                    <div className="w-5 h-5 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center mr-2 shadow-md">
                      <Palette className="w-3 h-3 text-white"/>
                    </div>
                    Brand Colors
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <ColorPicker name="brand_colors.primary" label="Primary" control={control} defaultValue={brandColors?.primary} />
                    <ColorPicker name="brand_colors.secondary" label="Secondary" control={control} defaultValue={brandColors?.secondary} />
                    <ColorPicker name="brand_colors.accent" label="Accent" control={control} defaultValue={brandColors?.accent} />
                    <ColorPicker name="brand_colors.neutral" label="Neutral" control={control} defaultValue={brandColors?.neutral} />
                  </div>
                </div>
              </div>
            </div>

            {/* Enhanced Brand Kit Fields */}
            <div className="bg-slate-50/50 dark:bg-slate-700/30 rounded-xl p-3 border border-slate-200/50 dark:border-slate-600/50">
              <h3 className="text-base font-bold text-slate-800 dark:text-slate-100 mb-4 flex items-center">
                <div className="w-6 h-6 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center mr-2 shadow-md">
                  <span className="text-white text-xs font-bold">✨</span>
                </div>
                Enhanced Brand Kit
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column: Industry, Target Audience, Primary Goal */}
                <div className="space-y-4">
                  <div>
                    <ModernDropdown
                      name="industry"
                      label="Industry"
                      control={control}
                      placeholder="Select industry..."
                      icon={Plus}
                      maxHeight="max-h-96"
                      optgroups={[
                        {
                          label: null, // No label for main categories
                          options: [
                            { value: "", label: "None" },
                            { value: "Beauty & Cosmetics", label: "Beauty & Cosmetics" },
                            { value: "Business Services", label: "Business Services" },
                            { value: "Fashion", label: "Fashion" },
                            { value: "Food & Beverage", label: "Food & Beverage" },
                            { value: "Health & Wellness", label: "Health & Wellness" },
                            { value: "Online Education", label: "Online Education" },
                            { value: "Real Estate", label: "Real Estate" },
                            { value: "Restaurants & Cafes", label: "Restaurants & Cafes" },
                            { value: "Sports & Fitness", label: "Sports & Fitness" },
                            { value: "Technology", label: "Technology" },
                            { value: "Travel & Tourism", label: "Travel & Tourism" }
                          ]
                        },
                        {
                          label: "Broader Categories",
                          options: [
                            { value: "Creative & Arts", label: "Creative & Arts" },
                            { value: "Professional Services", label: "Professional Services" },
                            { value: "Retail & E-commerce", label: "Retail & E-commerce" },
                            { value: "Manufacturing & Industrial", label: "Manufacturing & Industrial" },
                            { value: "Non-Profit & Community", label: "Non-Profit & Community" }
                          ]
                        },
                        {
                          label: null,
                          options: [
                            { value: "Other", label: "Other (Custom)" }
                          ]
                        }
                      ]}
                    />
                    
                    {/* Custom Industry Input - Shows when "Other" is selected */}
                    {watch('industry') === 'Other' && (
                      <motion.div 
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        className="mt-4"
                      >
                        <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">
                          Specify your industry
                        </label>
                        <div className="relative group">
                          <div className="absolute left-4 top-1/2 -translate-y-1/2 z-10">
                            <Palette className="w-4 h-4 text-slate-400 dark:text-slate-500 group-focus-within:text-indigo-500 dark:group-focus-within:text-indigo-400 transition-colors duration-200" />
                          </div>
                          <input
                            {...register('custom_industry')}
                            type="text"
                            placeholder="e.g., Pet Grooming, Vintage Collectibles, Drone Photography..."
                            className="w-full pl-11 pr-4 py-3 bg-white/80 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 dark:focus:border-indigo-400 transition-all duration-200 text-slate-900 dark:text-slate-100 placeholder-slate-500 dark:placeholder-slate-400 shadow-sm hover:shadow-md backdrop-blur-sm text-sm"
                          />
                        </div>
                        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400 flex items-center">
                          <span className="mr-1">💡</span>
                          Be specific to help AI generate more relevant content for your niche
                        </p>
                      </motion.div>
                    )}
                  </div>
                  
                  <ModernDropdown
                    name="target_audience"
                    label="Target Audience"
                    control={control}
                    placeholder="Select target audience..."
                    icon={Plus}
                    options={[
                      { value: "", label: "None" },
                      { value: "Young Adults (18-30)", label: "Young Adults (18-30)" },
                      { value: "Professionals (25-45)", label: "Professionals (25-45)" },
                      { value: "Families", label: "Families" },
                      { value: "Seniors (50+)", label: "Seniors (50+)" },
                      { value: "General", label: "General Audience" }
                    ]}
                  />
                  
                  <ModernDropdown
                    name="primary_goal"
                    label="Primary Goal"
                    control={control}
                    placeholder="Select primary goal..."
                    icon={Plus}
                    options={[
                      { value: "", label: "None" },
                      { value: "Increase Sales", label: "Increase Sales" },
                      { value: "Build Brand Awareness", label: "Build Brand Awareness" },
                      { value: "Drive Engagement", label: "Drive Engagement" },
                      { value: "Generate Leads", label: "Generate Leads" },
                      { value: "Viral Potential", label: "Viral Potential" },
                      { value: "Build Community", label: "Build Community" }
                    ]}
                  />
                </div>
                
                {/* Right Column: Brand Personality */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3">Brand Personality</label>
                  <div className="bg-white/50 dark:bg-slate-800/50 rounded-xl p-4 border border-slate-200/50 dark:border-slate-600/50 space-y-3">
                    {['Professional', 'Fun & Playful', 'Luxury', 'Eco-Friendly', 'Innovative', 'Trustworthy'].map((trait) => (
                      <label key={trait} className="flex items-center space-x-3 cursor-pointer group">
                        <div className="relative">
                          <input
                            type="checkbox"
                            value={trait}
                            {...register('brand_personality')}
                            className="w-5 h-5 text-purple-600 bg-white dark:bg-slate-800 border-2 border-slate-300 dark:border-slate-600 rounded-md focus:ring-purple-500 focus:ring-2 transition-all duration-200 cursor-pointer"
                          />
                        </div>
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors duration-200">{trait}</span>
                      </label>
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-slate-500 dark:text-slate-400 flex items-center">
                    <span className="mr-1">✨</span>
                    Select traits that best represent your brand's character
                  </p>
                </div>
              </div>
            </div>

            {/* Save as Template Checkbox */}
            <div className="pt-6 border-t border-slate-200 dark:border-slate-600">
              <div className="flex items-center space-x-3 mb-4">
                <input
                  type="checkbox"
                  id="saveAsTemplate"
                  checked={saveAsTemplate}
                  onChange={(e) => setSaveAsTemplate(e.target.checked)}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <label htmlFor="saveAsTemplate" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Save this brand kit as a template
                </label>
              </div>
              
              {saveAsTemplate && (
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Enter template name..."
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500"
                  />
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4">
              <motion.button
                type="button"
                onClick={() => navigate(`/app/clients`)}
                className="px-8 py-3 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 font-semibold rounded-xl border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600 transition-all duration-300 shadow-sm hover:shadow-md"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Cancel
              </motion.button>
              <motion.button
                type="submit"
                disabled={isLoading}
                className="flex items-center justify-center px-8 py-3 text-white font-semibold rounded-xl shadow-lg transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed relative overflow-hidden"
                style={{ 
                  background: 'linear-gradient(135deg, #30b3dd 0%, #4a298a 50%, #30b3dd 100%)',
                  backgroundSize: '200% 200%',
                  backgroundPosition: 'left center',
                  boxShadow: '0 4px 15px rgba(48, 179, 221, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1) inset'
                }}
                onMouseEnter={(e) => {
                  if (!isLoading) {
                    e.currentTarget.style.backgroundPosition = 'right center';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(48, 179, 221, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.2) inset';
                    e.currentTarget.style.transform = 'translateY(-1px) scale(1.02)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isLoading) {
                    e.currentTarget.style.backgroundPosition = 'left center';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(48, 179, 221, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1) inset';
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  }
                }}
                whileHover={{ scale: isLoading ? 1 : 1.02 }}
                whileTap={{ scale: isLoading ? 1 : 0.98 }}
              >
                {isLoading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Save className="w-5 h-5 mr-2" />}
                {isLoading ? 'Saving...' : 'Save Changes'}
              </motion.button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default BrandKitPage;
